import '@operato/data-tree/ox-tree.js'
import './ox-mapping-area.js'

import { LitElement, html, css, PropertyValues } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { provide } from '@lit/context'

import { OxTree } from '@operato/data-tree/ox-tree.js'

import { TreeNode, Mapping } from './types.js'
import { dataMapperContext, DataMapperContextType } from './context/data-mapper-context.js'
import { OxMappingArea } from './ox-mapping-area.js'

@customElement('ox-data-mapper')
export class OxDataMapper extends LitElement {
  static styles = css`
    .container {
      display: flex;
      width: 100%;
      min-height: 100%;
      position: relative;
      overflow-y: auto;
      align-items: stretch;
    }

    .tree-container {
      position: relative;
      flex: 1;
      display: flex;
      flex-direction: column;
      padding: 0;
      margin: 0;
    }

    .tree-container.source {
      order: 0;
      padding-right: 0px;
    }

    .tree-container.target {
      order: 2;
      padding-left: 0px;
      text-align: right;
    }

    ox-mapping-area {
      order: 1;
      position: relative;
      flex: 1;
      padding: 0;
      margin: 0;
      text-align: center;
      position: relative;
    }

    .tree-container.source::after,
    ox-mapping-area::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      width: 1px;
      height: 100%;
      background-color: black;
    }
  `

  @property({ type: Object }) sourceSchema: TreeNode = { id: 'source', label: 'source' }
  @property({ type: Object }) targetSchema: TreeNode = { id: 'target', label: 'target' }
  @property({ type: Array }) mappings: Mapping[] = []

  @state() private selected: any = null

  @query('.tree-container.source > ox-tree') private sourceTree!: OxTree
  @query('.tree-container.target > ox-tree') private targetTree!: OxTree
  @query('ox-mapping-area') private mappingsArea!: OxMappingArea

  @provide({ context: dataMapperContext })
  private dataMapperContext: DataMapperContextType = {
    getUniqueId: () => {
      return Math.random().toString(36).substring(2, 9)
    },
    updateMappings: (mappings: Mapping[]) => {
      this.mappings = mappings
      this.dispatchEvent(
        new CustomEvent('change', {
          detail: this.mappings
        })
      )
    },
    getNodePosition: this.getNodePosition.bind(this)
  }

  private dragStartElement: EventTarget | null = null

  render() {
    return html`
      <div class="container">
        <div class="tree-container source">
          <ox-tree
            .data=${this.sourceSchema}
            @collapsed=${() => this.mappingsArea.requestUpdate()}
            @expanded=${() => this.mappingsArea.requestUpdate()}
            @dragstart=${this.onDragStart}
            @dragover=${this.onDragOver}
            @drop=${this.onDrop}
            label-extends
          ></ox-tree>
        </div>

        <div class="tree-container target">
          <ox-tree
            .data=${this.targetSchema}
            @collapsed=${() => this.mappingsArea.requestUpdate()}
            @expanded=${() => this.mappingsArea.requestUpdate()}
            @dragstart=${this.onDragStart}
            @dragover=${this.onDragOver}
            @drop=${this.onDrop}
            direction="rtl"
            label-extends
          ></ox-tree>
        </div>

        <ox-mapping-area .mappings=${this.mappings}></ox-mapping-area>
      </div>
    `
  }

  getNodePosition(nodeId: string, source: boolean) {
    const tree = source ? this.sourceTree : this.targetTree
    if (!tree) {
      return
    }

    const nodeRect = tree.findNodeSelfBounds(nodeId)
    if (!nodeRect) {
      return
    }
    const mappingAreaRect = this.mappingsArea.getBoundingClientRect()

    return {
      x: source ? 0 : mappingAreaRect.width,
      y: nodeRect.top - mappingAreaRect.top + nodeRect!.height / 2
    }
  }

  onDragStart(e: DragEvent) {
    const path = e.composedPath()
    const target = path.find(el => (el as HTMLElement).dataset?.id) as HTMLElement | undefined
    const nodeId = target?.dataset?.id

    if (!nodeId) {
      return
    }

    const from = e.target === this.sourceTree ? 'source' : 'target'

    e.dataTransfer!.setData('application/node-id', nodeId)
    e.dataTransfer!.setData('application/from', from)
    e.dataTransfer!.effectAllowed = 'move'

    // ✅ 노드의 라벨을 찾아 미리보기로 사용
    const nodeLabel = target?.querySelector('span[label]')?.textContent || nodeId

    // ✅ 미리보기 요소 생성
    const dragPreview = document.createElement('div')
    dragPreview.textContent = nodeLabel
    dragPreview.style.fontSize = '14px'
    dragPreview.style.padding = '4px 8px'
    dragPreview.style.background = 'rgba(255, 255, 255, 0.3)'
    dragPreview.style.borderRadius = '4px'
    dragPreview.style.position = 'absolute'
    dragPreview.style.top = '-100px' // 화면 밖으로 배치 (임시)
    dragPreview.style.left = '-100px'
    dragPreview.style.pointerEvents = 'none'
    dragPreview.style.zIndex = '1000'

    document.body.appendChild(dragPreview)

    // ✅ 커스텀 드래깅 이미지 적용
    e.dataTransfer!.setDragImage(dragPreview, 0, 0)

    this.dragStartElement = e.target

    // ✅ 일정 시간 후 미리보기 요소 삭제
    setTimeout(() => document.body.removeChild(dragPreview), 0)
  }

  onDragOver(e: DragEvent) {
    e.preventDefault()

    const from = e.dataTransfer!.getData('application/from')

    if (this.dragStartElement === e.target) {
      return
    }

    const path = e.composedPath()
    const target = path.find(el => (el as HTMLElement).dataset?.id) as HTMLElement | undefined
    const nodeId = target?.dataset?.id

    if (!nodeId) return
    ;(e.target as OxTree).selected = (target as any).item
  }

  onDrop(e: DragEvent) {
    e.preventDefault()

    const nodeId = e.dataTransfer!.getData('application/node-id')
    const from = e.dataTransfer!.getData('application/from')

    if (
      !nodeId ||
      (from == 'source' && e.target !== this.targetTree) ||
      (from == 'target' && e.target !== this.sourceTree)
    ) {
      return
    }

    // 드롭된 타겟 노드 찾기
    const path = e.composedPath()
    const targetNode = path.find(el => (el as HTMLElement).dataset?.id) as HTMLElement | undefined
    const targetNodeId = targetNode?.dataset?.id

    if (!targetNodeId) {
      return
    }

    // 중복 매핑 확인
    const existingMapping = (this.mappings || []).find(
      m =>
        m.inputs.includes(from == 'source' ? nodeId : targetNodeId) &&
        m.outputs.includes(from == 'source' ? targetNodeId : nodeId)
    )

    if (!existingMapping) {
      const { width, height } = this.mappingsArea.getBoundingClientRect()
      const { y: sourceY = 0 } =
        this.dataMapperContext.getNodePosition(from == 'source' ? nodeId : targetNodeId, true) || {}
      const { y: targetY = height } =
        this.dataMapperContext.getNodePosition(from == 'target' ? nodeId : targetNodeId, false) || {}

      const newMapping: Mapping = {
        id: this.dataMapperContext.getUniqueId(),
        mappingFunction: 'passthrough',
        inputs: [from == 'source' ? nodeId : targetNodeId],
        outputs: [from == 'source' ? targetNodeId : nodeId],
        position: {
          x: width / 2 - 50,
          y: (sourceY + targetY) / 2
        }
      }

      this.dataMapperContext.updateMappings([...(this.mappings || []), newMapping])

      this.mappingsArea.selectMappingBox(newMapping)
    }
  }
}
